import { useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/shared/lib/supabase'
import type { Project } from '@/shared/types'

interface UpdateProjectData {
  name: string
  description: string
  objective: string
  target_users: string
  existing_assets: string
  constraints?: string
  inspirations?: string
}

async function updateProject(
  projectId: string,
  data: UpdateProjectData
): Promise<Project> {
  // Update project and clear rejection_reason to mark as resubmitted
  const { data: project, error } = await supabase
    .from('projects')
    .update({
      ...data,
      rejection_reason: null, // Clear rejection on resubmit
      updated_at: new Date().toISOString(),
    })
    .eq('id', projectId)
    .select()
    .single()

  if (error) {
    throw new Error(error.message)
  }

  return project as Project
}

export function useUpdateProject(projectId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: UpdateProjectData) => updateProject(projectId, data),
    onSuccess: (project) => {
      // Invalidate project queries
      queryClient.invalidateQueries({ queryKey: ['project'] })
      queryClient.invalidateQueries({ queryKey: ['teams'] })
      // Update the specific project in cache
      queryClient.setQueryData(['project', 'short', project.short_id], project)
    },
  })
}
